/**
 * headlight-qrcode-web — attach HeadLight QR scanning, printing and form inputs to a pict app.
 *
 *     const HeadlightQRCodeBundle = require('headlight-qrcode-web');
 *     new HeadlightQRCodeBundle(pict).attach({ HeadlightQR: { EntitySource, DestinationResolver, Wedge: { Enabled: true } } });
 *
 * Plain pict-section-form apps get the `QRCode` input under the default template prefixes; HeadLight
 * apps on the Bulma themes pass `TemplatePrefixes` for their Edit/Read/Print themes.
 */

const libLabels = require('./labels/Label-Renderer.js');

/** @type {Record<string, any>} */
const providers =
{
	'HeadlightQR': require('./providers/Pict-Provider-HeadlightQR.js'),
	'HeadlightQR-Input': require('./providers/Pict-Provider-HeadlightQR-Input.js'),
};

/** @type {Record<string, any>} */
const views =
{
	'HeadlightQR-ModeBar': require('./views/PictView-HeadlightQR-ModeBar.js'),
	'HeadlightQR-Camera': require('./views/PictView-HeadlightQR-Camera.js'),
	'HeadlightQR-ResultSheet': require('./views/PictView-HeadlightQR-ResultSheet.js'),
	'HeadlightQR-PrintDialog': require('./views/PictView-HeadlightQR-PrintDialog.js'),
};

const templates = require('./templates/InputType-QRCode.js');

/** Default prefixes: plain pict-section-form editable and read-only template sets. */
const DEFAULT_TEMPLATE_PREFIXES =
{
	Edit: [ 'Pict-MT-Base' ],
	Read: [ 'Pict-Forms-ReadOnly' ],
	Print: [],
};

/** Prefixes for HeadLight apps using headlight-dynamic-inputs-bundle themes. */
const HEADLIGHT_THEME_PREFIXES =
{
	Edit: [ 'PictProvider-Theme-Edit' ],
	Read: [ 'PictProvider-Theme-Read' ],
	Print: [ 'PictProvider-Theme-Print' ],
};

const INPUT_CSS = /*css*/`
.hlqr-input { margin: 0.5rem 0 1rem; }
.hlqr-input-label { display: block; font-weight: 600; margin-bottom: 0.35rem; }
.hlqr-input-body { display: flex; gap: 1rem; align-items: flex-start; flex-wrap: wrap; }
.hlqr-input-image { line-height: 0; }
.hlqr-input-image svg { display: block; }
.hlqr-input-empty { display: flex; align-items: center; justify-content: center; border: 1px dashed var(--theme-color-border-default, #c5ccd3); border-radius: 6px; font-size: 0.8rem; line-height: 1.2; opacity: 0.7; text-align: center; }
.hlqr-input-controls { flex: 1 1 14rem; min-width: 12rem; }
.hlqr-input-text { width: 100%; padding: 0.4rem 0.55rem; border: 1px solid var(--theme-color-border-default, #c5ccd3); border-radius: 6px; font-family: ui-monospace, Menlo, Consolas, monospace; }
.hlqr-input-value { font-family: ui-monospace, Menlo, Consolas, monospace; word-break: break-all; }
.hlqr-input-buttons { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.5rem; }
.hlqr-input-button { border: 1px solid var(--theme-color-border-default, #c5ccd3); background: transparent; color: inherit; border-radius: 6px; padding: 0.3rem 0.75rem; cursor: pointer; font-size: 0.85rem; }
.hlqr-input-message { font-size: 0.85rem; margin-top: 0.35rem; color: #b91c1c; }
`;

class HeadlightQRCodeBundle
{
	static getProviderMap()
	{
		return providers;
	}

	static getViewMap()
	{
		return views;
	}

	/** @return {{ Edit: Array<any>, Read: Array<any>, Print: Array<any> }} */
	static getTemplates()
	{
		return templates;
	}

	/**
	 * @param {any} pPict
	 */
	constructor(pPict)
	{
		this.pict = pPict;
	}

	/**
	 * Register providers, views, input templates and styles.
	 *
	 * @param {Object} [pOptions]
	 * @param {Object} [pOptions.HeadlightQR] - Options for the HeadlightQR provider.
	 * @param {Record<string, Object>} [pOptions.Views] - Options per view hash.
	 * @param {{ Edit?: Array<string>, Read?: Array<string>, Print?: Array<string> }} [pOptions.TemplatePrefixes]
	 * @return {any} the HeadlightQR provider
	 */
	attach(pOptions)
	{
		const tmpOptions = pOptions || {};
		this.pict.addProviderSingleton('HeadlightQR', tmpOptions.HeadlightQR || {}, providers.HeadlightQR);
		this.pict.addProviderSingleton('HeadlightQR-Input', {}, providers['HeadlightQR-Input']);
		for (const [ tmpHash, tmpView ] of Object.entries(views))
		{
			this.pict.addViewSingleton(tmpHash, (tmpOptions.Views && tmpOptions.Views[tmpHash]) || {}, tmpView);
		}
		this.injectTemplates(tmpOptions.TemplatePrefixes || DEFAULT_TEMPLATE_PREFIXES);
		this.pict.CSSMap.addCSS('HeadlightQR-Input', INPUT_CSS);
		this.pict.CSSMap.addCSS('HeadlightQR-Labels', libLabels.LABEL_CSS);
		return this.pict.providers.HeadlightQR;
	}

	/**
	 * @param {{ Edit?: Array<string>, Read?: Array<string>, Print?: Array<string> }} pPrefixes
	 */
	injectTemplates(pPrefixes)
	{
		// Templates are added one by one: injectTemplateSet would also re-add every pict-section-form
		// default under the prefix, overwriting a host theme's own templates.
		const tmpDynamicInput = this.pict.providers.DynamicInput;
		for (const tmpTheme of [ 'Edit', 'Read', 'Print' ])
		{
			for (const tmpPrefix of (pPrefixes[tmpTheme] || []))
			{
				for (const tmpTemplate of templates[tmpTheme])
				{
					const tmpHash = `${tmpPrefix}${tmpTemplate.HashPostfix}`;
					this.pict.TemplateProvider.addTemplate(tmpHash, tmpTemplate.Template);
					if (tmpDynamicInput)
					{
						for (const tmpExtension of tmpTemplate.DefaultInputExtensions)
						{
							tmpDynamicInput.addDefaultInputProvider(tmpHash, tmpExtension);
						}
					}
				}
			}
		}
	}
}

module.exports = HeadlightQRCodeBundle;
module.exports.DEFAULT_TEMPLATE_PREFIXES = DEFAULT_TEMPLATE_PREFIXES;
module.exports.HEADLIGHT_THEME_PREFIXES = HEADLIGHT_THEME_PREFIXES;
module.exports.RestEntitySource = require('./entity-sources/Rest-Entity-Source.js');
module.exports.Labels = libLabels;
module.exports.MediaPresets = require('./labels/Label-Media-Presets.js');
module.exports.QRMatrix = require('./rendering/QR-Matrix.js');
module.exports.WedgeBurstDetector = require('./scanning/Wedge-Burst-Detector.js');
module.exports.ScanGeolocation = require('./scanning/Scan-Geolocation.js');
